import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { Alert, Input, InputGroup, InputGroupText, Spinner } from 'reactstrap';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';
import { ApplicationState } from 'store/redux';
import * as AuthenticationStore from 'store/redux/Authentication';
import { IAuthenticationActionCreators } from 'store/redux/Authentication';
import Context from 'configuration/context';
import { authenticationServiceFactory } from 'services/authenticationService';
import { CustomerFiles } from 'components';
import { filesServiceFactory, IFile } from 'services/filesService';
import 'services/filesService';
import heroImage from 'assets/images/hero-fakturaunderlag.jpg';
import apiClient, { ApiError } from '../clients/ApiClient';
import { ErrorState } from '../models/errors';

export type IFilesProps = IAuthenticationActionCreators & {
  authenticationState?: AuthenticationStore.UserState;
};

const Files: React.FunctionComponent<React.PropsWithChildren<IFilesProps>> = ({
  setLoginState,
  authenticationState,
}: React.PropsWithChildren<IFilesProps>) => {
  const { t, ready } = useTranslation('translation');
  const [files, setFiles] = useState([] as IFile[]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredFiles, setFilteredFiles] = useState<IFile[]>();
  const [fileFilterTerm, setFileFilterTerm] = useState('');
  const LoginState = authenticationState?.LoginState;
  const customerId = authenticationState?.UserInRole?.selectedCustomer?.customerId;
  const [busyMessage, setBusyMessage] = useState<string>('filearea.FETCHING_FILES');
  const [error, setError] = useState<ErrorState>({ msgKey: '' });

  useEffect(() => {
    if (ready) setBusyMessage('filearea.FETCHING_FILES');
  }, [ready]);

  useEffect(() => {
    if (LoginState && LoginState === AuthenticationStore.LoginState.LOGGED_IN && customerId) {
      filesServiceFactory(
        Context.APIUrl + '/v1',
        authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
      )
        .loadFiles(customerId.toString())
        .then((files: any) => {
          setIsLoading(false);
          setFiles(files.items);
          setFilteredFiles(files.items);
        })
        .catch((error: any) => {
          console.error('Load file error: ', error);
          Sentry.captureException(error);
          setIsLoading(false);
          setFiles([]);
        });
    }
  }, [customerId, LoginState, authenticationState, setLoginState]);

  useEffect(() => {
    var _fileFilterTerm = fileFilterTerm.toLowerCase();
    setFilteredFiles(
      _fileFilterTerm.length > 0
        ? files.filter(
            (f) =>
              f.fileName.toLowerCase().indexOf(_fileFilterTerm) >= 0 ||
              dayjs(f.created).format('YYYY-MM-DD').toLowerCase().startsWith(_fileFilterTerm) ||
              f.createdBy.toLowerCase().indexOf(_fileFilterTerm) >= 0
          )
        : [...files]
    );
  }, [files, fileFilterTerm]);

  const downloadFile = async (fileId: number, fileName: string) => {
    setError({ msgKey: '' });
    if (
      !authenticationState?.LoginState ||
      authenticationState.LoginState !== AuthenticationStore.LoginState.LOGGED_IN
    ) {
      return;
    }

    // I'm sorry :( But this will change when we change auth provider
    const tokenProvider = authenticationServiceFactory(
      Context.APIUrl,
      { setLoginState },
      authenticationState
    );

    try {
      const fileBlob = await apiClient.withAuth(tokenProvider).getCustomerFile(customerId!, fileId);

      const link = window.URL.createObjectURL(fileBlob);
      const a = document.createElement('a') as HTMLAnchorElement;
      a.href = link;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(link);
    } catch (error: any) {
      const apiApiError = error as ApiError;
      if (apiApiError.status === 401) {
        // Not need to show error user should be logged in
        return;
      }

      setError({ msgKey: 'filearea.problem.Generic' });
      Sentry.captureException(error);

      console.error(error);
    }
  };

  const updateFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setFileFilterTerm(event.currentTarget.value);
  };

  return (
    <>
      <div className="row limited">
        <div className="col-12 image-container">
          <img src={heroImage} alt="" />
        </div>
      </div>
      <div className="row limited filearea">
        <div className="col mt-4">
          <div className="files-content">
            <div className="row">
              <div className="col-md-8 col-12">
                <p>
                  <Trans ns="translation" i18nKey="filearea.INFO">
                    Här finner du kundspecifika rutiner- och informationsdokument som har laddats
                    upp för hela din organisation från Avonova. Alla utsedda beställare i din
                    organisation som har tillgång till Avonovas Webbportal ser samtliga uppladdade
                    dokument.
                    <p className="important">
                      OBS! Här får man inte ladda upp individrelaterade dokument med känslig
                      information. Vi rekommenderas att ni använder er av meddelandefunktionen i
                      portalen.{' '}
                    </p>
                  </Trans>
                </p>
              </div>
              <div className="col-md-4 col-12">
                <InputGroup className="search-container">
                  <InputGroupText>
                    <i className="fa fa-search" />
                  </InputGroupText>
                  <Input placeholder={t('filearea.SEARCH')} onKeyUp={updateFilter} />
                </InputGroup>
              </div>
              {error.msgKey && (
                <Alert key="danger" variant="danger" className="alert-danger">
                  <Trans i18nKey={error.msgKey} ns="translation"></Trans>
                </Alert>
              )}
              {isLoading ? (
                <h1 className="loading-indicator-container col-12">
                  <Spinner />
                  {t(busyMessage)}...
                </h1>
              ) : (
                <div className="col-12">
                  <CustomerFiles onDownload={downloadFile} items={filteredFiles} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(
  (appState: ApplicationState) => ({
    authenticationState: appState.authentication,
  }),
  { ...AuthenticationStore.actions }
)(Files);
